import axios from 'axios';

import {
  signAPI,
  userAPI,
  storyAPI,
  studioAPI,
  donationAPI,
  storeAPI,
} from 'app/apis';

import { STORY_AD_TYPES } from 'app/utils/constants';

import * as t from './types';

const { API_URL } = process.env;

const socialSignin = (socialType, accessToken, idToken) => ({
  type: t.SOCIAL_SIGNIN,
  payload: signAPI.socialSignin(socialType, accessToken, idToken),
});

const fetchMyInfo =
  (firebaseIdToken = null, saveToken = true, skipCertification = false) =>
  (dispatch, getState) => {
    const { platform } = getState().common;

    const action = {
      type: t.FETCH_USER_INFO,
      payload: userAPI.getMyInfo(),
      meta: { firebaseIdToken, saveToken },
    };

    if (!skipCertification && !platform) {
      dispatch(fetchMyAgeGrade(firebaseIdToken)).catch(() => {});
      dispatch(fetchMyPhone(firebaseIdToken)).catch(() => {});
    }

    return dispatch(action);
  };

const socialSignup = (data) => ({
  type: t.SOCIAL_SIGNUP,
  payload: signAPI.socialSignup(data),
  skipError: true,
});

const emailSignup = (data) => ({
  type: t.EMAIL_SIGNUP,
  payload: signAPI.emailSignup(data),
  skipError: true,
});

const checkSign = (key, value) => ({
  type: t.CHECK_SIGN,
  payload: signAPI.checkSign(key, value),
  skipError: true,
});

const recoveryUser = (value) => ({
  type: t.RECOVERY_USER,
  payload: signAPI.recoveryUser(value),
  skipError: true,
});

const recoveryUserByPhone = (transactionId) => ({
  type: t.RECOVERY_USER_BY_PHONE,
  payload: signAPI.recoveryUserByPhone(transactionId),
  skipError: true,
});

const sendResetPassword = (value) => ({
  type: t.SEND_RESET_PASSWORD,
  payload: signAPI.sendResetPassword(value),
  skipError: true,
});

const updateMe = (user) => ({
  type: t.UPDATE_ME,
  meta: { user },
});

const addCoin = (coin) => (dispatch, getState) => {
  const {
    user: { isLoaded, data: user },
  } = getState().common;

  if (!isLoaded) {
    return;
  }

  dispatch(
    updateMe({
      coin: user.coin + coin,
    })
  );
};

const addBalloon =
  (balloon, type = null) =>
  (dispatch, getState) => {
    const {
      user: { isLoaded, data: user },
    } = getState().common;

    if (!isLoaded) {
      return;
    }

    dispatch(
      updateMe({
        balloon: user.balloon + balloon,
        balloonDetail: {
          ...user.balloonDetail,
          ...(type
            ? {
                [type]: user.balloonDetail[type] + balloon,
              }
            : {}),
        },
      })
    );
  };

const changeMyInfo = (data) => ({
  type: t.CHANGE_MY_INFO,
  payload: userAPI.patchUserInfo(data),
});

const unregisterUser = () => ({
  type: t.UNREGISTER_USER,
  payload: userAPI.unregisterUser(),
});

const postReasons = (type, reasons, rating = null) => ({
  type: t.POST_REASONS,
  payload: userAPI.postReasons(type, reasons, rating),
});

const signOut = () => ({
  type: t.SIGN_OUT,
  payload: signAPI.signOut(),
});

const resetUser = (retry = false) => ({
  type: t.RESET_USER,
  retry,
});

const showStorageWarning = () => ({
  type: t.SHOW_STORAGE_WARNING,
});

const hideStorageWarning = () => ({
  type: t.HIDE_STORAGE_WARNING,
});

// ab test
const setExperiments = (data) => ({
  type: t.SET_EXPERIMENTS,
  data,
});

const setShowAdTapCount = (tapCount) => ({
  type: t.SET_SHOW_AD_TAP_COUNT,
  tapCount,
});

const updateABTestDataReceived = (isDataReceived) => ({
  type: t.UPDATE_AB_TEST_DATA_RECEIVED,
  isDataReceived,
});

// adMob
const setAdLoadStatus = (adType, status) => ({
  type: t.SET_AD_LOAD_STATUS,
  adType,
  status,
});

// promotion
const checkPromotions = () => ({
  type: t.CHECK_PROMOTIONS,
  payload: userAPI.checkPromotions(),
});

const setPromotions = (promotions) => ({
  type: t.SET_PROMOTIONS,
  promotions,
});

const fetchFirebaseToken = () => ({
  type: t.FETCH_FIREBASE_TOKEN,
  payload: userAPI.getFirebaseToken(),
});

const fetchUserMarketItems =
  (userIds, forceFetch = false) =>
  (dispatch, getState) => {
    const { userMarketItems } = getState().common;
    const newUserIds = userIds.filter((userId) =>
      userMarketItems[userId] ? !userMarketItems[userId].isLoaded : true
    );

    if (newUserIds.length === 0 && !forceFetch) {
      return Promise.resolve();
    }
    return dispatch({
      type: t.FETCH_USERS_MARKET_ITEMS,
      payload: userAPI.getUsersMarketItems(forceFetch ? userIds : newUserIds),
      meta: { userIds: forceFetch ? userIds : newUserIds, forceFetch },
    });
  };

const fetchUserFans =
  (id, userIds, force = false) =>
  (dispatch, getState) => {
    const { userFans } = getState().common;

    if (!force) {
      const newUserIds = userIds.filter((userId) => {
        const key = `${userId}:${id}`;
        return userFans[key] ? !userFans[key].isLoaded : true;
      });

      if (newUserIds.length === 0) {
        return Promise.resolve();
      }

      return dispatch({
        type: t.FETCH_USER_FANS,
        payload: userAPI.getUserFans(id, newUserIds),
        skipError: true,
        meta: { userIds: newUserIds, targetUserId: id },
      });
    }

    const notFanUserIds = userIds.filter((userId) => {
      const key = `${userId}:${id}`;
      return userFans[key] ? !userFans[key].data.isFan : true;
    });

    if (notFanUserIds.length === 0) {
      return Promise.resolve();
    }

    return dispatch({
      type: t.FETCH_USER_FANS,
      payload: userAPI.getUserFans(id, notFanUserIds),
      skipError: true,
      meta: { userIds: notFanUserIds, targetUserId: id },
    });
  };

const fetchUserSetting =
  (objectType, objectId, force = false) =>
  (dispatch, getState) => {
    const { userSettings } = getState().common;
    if (!force && userSettings[`${objectType}:${objectId}`]) {
      return Promise.resolve();
    }

    return dispatch({
      type: t.FETCH_USER_SETTING,
      payload: userAPI.getUserSetting(objectType, objectId),
      meta: { objectType, objectId },
      skipError: true,
    });
  };

const setUserSetting = (objectType, objectId, key, value) => ({
  type: t.SET_USER_SETTING,
  payload: userAPI.postUserSetting(objectType, objectId, key, value),
  meta: { objectType, objectId, key },
});

const fetchStoryAd =
  (storyId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      latestMyStory: { isLoaded: isLatestMyStoryLoaded },
    } = state.common;
    const {
      location: { pathname, search },
    } = state.router;
    const url = `${pathname}${search}`;

    return dispatch({
      type: t.FETCH_STORY_AD,
      payload: storyAPI.getStoryAd(STORY_AD_TYPES.LIST, storyId),
      meta: { url },
      skipError: true,
    }).then(({ value }) => {
      const { result } = value;
      if (!result.id && !isLatestMyStoryLoaded) {
        return dispatch({
          type: t.FETCH_LATEST_MY_STORY,
          payload: studioAPI.getLatestMyStory(),
          skipError: true,
        }).then(() => ({ value }));
      }

      return { value };
    });
  };

const setStoryAdFull = (data) => ({
  type: t.SET_STORY_AD_FULL,
  data,
});

const fetchStoryAdFull =
  (storyId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      storyAdFull: { isLoaded: isStoryAdLoaded, isViewed: isStoryAdViewed },
    } = state.common;
    let poolKey = null;
    if (isStoryAdLoaded && !isStoryAdViewed) {
      return Promise.resolve({ value: { result: {} } });
    }

    return dispatch({
      type: t.FETCH_STORY_AD_FULL,
      payload: storyAPI.getStoryAd(STORY_AD_TYPES.FULL, storyId),
      skipError: true,
    })
      .then(({ value }) => {
        const { result } = value;
        if (!result.id) {
          return Promise.reject();
        }

        const {
          id: storyAdId,
          story: { id: _storyId },
          episode: { id: episodeId },
          poolKey: _poolKey,
        } = result;
        poolKey = _poolKey;

        return axios.get(
          `${API_URL}/stories/${_storyId}/episodes/${episodeId}/`,
          {
            storyAdId,
            storyId: _storyId,
            episodeId,
          }
        );
      })
      .then(({ data, config }) => {
        const {
          content: {
            attachment: { url: contentUrl },
          },
          story: { title: _title },
        } = data.result;

        return axios.get(contentUrl, { ...config, title: _title });
      })
      .then(({ data, config }) => {
        const _data = {
          content: data,
          storyAdId: config.storyAdId,
          storyId: config.storyId,
          episodeId: config.episodeId,
          title: config.title,
          isDefaultAd: false,
          ...(poolKey ? { poolKey } : {}),
        };
        dispatch(setStoryAdFull(_data));

        return _data;
      })
      .catch(() => {
        dispatch(
          setStoryAdFull({
            isDefaultAd: true,
          })
        );
      });
  };

const viewStoryAdFull = () => ({
  type: t.VIEW_STORY_AD_FULL,
});

const resetStoryAdFull = () => ({
  type: t.RESET_STORY_AD_FULL,
});

const clickStoryAd = (storyAdId, type = STORY_AD_TYPES.LIST) => ({
  type: t.CLICK_STORY_AD,
  payload: storyAPI.clickStoryAd(storyAdId, type),
});

const setValue = (key, value) => ({
  type: t.SET_VALUE,
  key,
  value,
});

const setLoading = (isLoading) => ({
  type: t.SET_LOADING,
  isLoading,
});

const fetchMyPhone = (firebaseIdToken = null) => ({
  type: t.FETCH_MY_PHONE,
  payload: userAPI.getMyCertification('PL'),
  meta: { firebaseIdToken },
  skipError: true,
});

const fetchMyAgeGrade = (firebaseIdToken = null) => ({
  type: t.FETCH_MY_AGE_GRADE,
  payload: userAPI.getMyCertification('AC'),
  meta: { firebaseIdToken },
  skipError: true,
});

const fetchVouchers = (voucherType) => ({
  type: t.FETCH_VOUCHERS,
  payload: userAPI.getMyVouchers(voucherType),
  meta: { voucherType },
  skipError: true,
});

const fetchVoucherRecommendation = (
  voucherRecommendationType,
  externalId = null
) => ({
  type: t.FETCH_VOUCHER_RECOMMENDATION,
  skipError: true,
  payload: userAPI.getMyVoucherRecommendation(
    voucherRecommendationType,
    externalId
  ),
});

const postVoucher = (voucherType, id) => ({
  type: t.POST_VOUCHER,
  payload: userAPI.postVoucher(id),
  meta: { voucherType, id },
});

const usedUpVoucher = (voucherType, id) => ({
  type: t.USED_UP_VOUCHER,
  meta: { voucherType, id },
});

const postUniversalBalloon = (balloon, description, transactionId) => ({
  type: t.POST_UNIVERSAL_BALLOON,
  payload: storeAPI.postUniversalBalloon(balloon, description, transactionId),
  meta: { balloon },
});

const postMyCertification = (transactionId, type = 'PL') => ({
  type: t.POST_MY_CERTIFICATION,
  payload: userAPI.postMyCertification(transactionId, type),
  meta: { type },
});

const fetchMyLatestStory = () => ({
  type: 'JUST_FETCH',
  payload: studioAPI.getLatestMyStory(),
  skipError: true,
});

const fetchMySocial = () => ({
  type: t.FETCH_MY_SOCIAL,
  payload: userAPI.getMySocial(),
});

const postMySocialConnect = (social, accessToken, idToken) => ({
  type: t.POST_MY_SOCIAL_CONNECT,
  payload: userAPI.postMySocialConnect(social, accessToken, idToken),
  meta: { social },
});

const postMySocialDisconnect = (social) => ({
  type: t.POST_MY_SOCIAL_DISCONNECT,
  payload: userAPI.postMySocialDisconnect(social),
  meta: { social },
});

const patchMyEmail = (email) => ({
  type: 'JUST_PATCH',
  payload: userAPI.patchMyEmail(email),
});

const postMyEmailVerification = () => ({
  type: 'JUST_POST',
  payload: userAPI.postMyEmailVerification(),
});

const fetchRecommendationNickname = () => ({
  type: 'JUST_FETCH',
  payload: userAPI.getRecommendationNickname(),
});

export default {
  socialSignin,
  fetchMyInfo,
  socialSignup,
  emailSignup,
  checkSign,
  recoveryUser,
  recoveryUserByPhone,
  sendResetPassword,
  updateMe,
  addCoin,
  addBalloon,
  changeMyInfo,
  unregisterUser,
  postReasons,
  signOut,
  resetUser,
  showStorageWarning,
  hideStorageWarning,
  setExperiments,
  updateABTestDataReceived,
  setAdLoadStatus,
  checkPromotions,
  setPromotions,
  setShowAdTapCount,
  fetchFirebaseToken,
  fetchUserMarketItems,
  fetchUserFans,
  fetchUserSetting,
  setUserSetting,
  fetchStoryAd,
  fetchStoryAdFull,
  setStoryAdFull,
  viewStoryAdFull,
  resetStoryAdFull,
  clickStoryAd,
  setValue,
  setLoading,
  fetchMyPhone,
  fetchMyAgeGrade,
  fetchVouchers,
  fetchVoucherRecommendation,
  postVoucher,
  usedUpVoucher,
  postUniversalBalloon,
  postMyCertification,
  fetchMyLatestStory,
  fetchMySocial,
  postMySocialConnect,
  postMySocialDisconnect,
  patchMyEmail,
  postMyEmailVerification,
  fetchRecommendationNickname,
};



// WEBPACK FOOTER //
// ./src/app/redux/common/actions.js